import type { FrameMessage } from "./frame-messages";
import { checkoutReducer } from "./state-machine";
import type { CheckoutEvent } from "./state-machine";
import type { SavedCard } from "./wallet-api";
import { checkoutReactions } from "./checkout-reactions";
import type {
  CheckoutCoreDeps,
  CoreContext,
  SessionEvent,
  StatePatch,
} from "./context";
import type { CardIsland } from "./ports";
import { NO_BROKER } from "./ports";
import { initialRefs } from "./context";
import { createEmailFlows } from "./email-flows";
import { money } from "./format";
import { createIdentityFlows } from "./identity-flows";
import { createPayFlows } from "./pay-flows";
import { createPayTimeline } from "@libs/checkout-status";
import { createReactions } from "./reactions";
import type { Reaction } from "./reactions";
import type {
  CheckoutConfig,
  CheckoutInputs,
  CheckoutSnapshot,
  CoreConfig,
  CoreState,
} from "./state";
import { initialState, isZeroTotal, sessionOf, snapshotOf } from "./state";

/**
 * The Lissen checkout without a screen.
 *
 * Everything the checkout knows and does — the session and its hold, who is
 * buying, the address, paying and the bank's checks, the wait for the
 * tickets, and the Pay button's story — lives here, in plain TypeScript
 * behind injected ports (ports.ts). The card itself is the card island's
 * (card-island.ts): the core only ever sees what it hands back. A renderer
 * reads `getSnapshot()`, re-reads it whenever `subscribe` calls, and turns
 * the buyer's presses into `actions`. The web renderer is
 * `apps/ticketing-checkout/src/checkout.tsx`; a native one reads the same
 * snapshot and draws the same steps on the same timings (motion.ts).
 *
 * State changes are visible at once; the side effects they set off run on
 * the next turn of the clock (a zero-delay timer), in the order they are
 * declared (checkout-reactions.ts) — the way a component's effects follow
 * its render, after the promise callbacks already under way have settled.
 */

export type CheckoutCoreOptions = CheckoutConfig & { inputs: CheckoutInputs };

export type CheckoutActions = {
  /** A keystroke in the address field. */
  typeEmail: (value: string) => void;
  /** The field lost focus: a valid address settles into the chip. */
  settleEmail: () => void;
  /** Enter in the field: settle and tell the engine now. */
  submitEmail: () => void;
  /** The × on the destination chip: host mode hands the change back to the
   *  page; elsewhere the address (and a Lissen sign-in) goes. */
  changeContact: () => void;
  /** Skip the Lissen code and pay as a guest. */
  continueAsGuest: () => void;
  /** Check a code; rejects when it did not match. */
  submitCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  /** Put a message in the one place errors go, or clear it. */
  showNotice: (message: string | null) => void;
  setRemember: (on: boolean) => void;
  selectCard: (id: string | null) => void;
  paySaved: (card: SavedCard) => void;
  /** Pay with the card typed in the card island; `brandHint` is what its
   *  fields last said. */
  payWithNewCard: (card: CardIsland, brandHint: string | null) => void;
  /** A wallet sheet (or the card fields) is confirming before a method
   *  exists: the form gives way to the payment's status meanwhile. */
  methodPending: (on: boolean) => void;
  /** A wallet sheet made a method: pay with it. */
  payExpress: (
    paymentMethodId: string,
    sheetEmail: string | null,
    label: string | null,
  ) => void;
  /** A wallet sheet or the card fields failed before paying. */
  paymentFailed: (message: string) => void;
  /** The bank's page said it is done. */
  bankDone: () => void;
  /** A free order: issue the tickets. */
  completeFree: () => void;
  /** A field took focus at this place in the component. */
  fieldFocused: (rect: { top: number; bottom: number }) => void;
};

export type CheckoutCore = {
  getSnapshot: () => CheckoutSnapshot;
  subscribe: (listener: () => void) => () => void;
  /** The page changed what it hands over (the locale). */
  update: (inputs: Partial<CheckoutInputs>) => void;
  actions: CheckoutActions;
  /** Mounted: the core's side effects begin (or carry on). Idempotent. */
  start: () => void;
  /** Unmounted: every timer, poll and subscription stops, and a later
   *  `start` picks up from the same state — React may mount twice. */
  stop: () => void;
  /** Gone for good. */
  dispose: () => void;
};

export function createCheckoutCore(
  deps: CheckoutCoreDeps,
  options: CheckoutCoreOptions,
): CheckoutCore {
  const { inputs, ...given } = options;
  const config: CoreConfig = {
    ...given,
    hostAnswers: deps.identity.handed === true || !deps.identity.broker,
  };
  const { broker } = deps.identity;
  const brokerView = () => broker?.current() ?? NO_BROKER;
  let state: CoreState = initialState(brokerView(), inputs);
  let snapshot: CheckoutSnapshot | null = null;
  const listeners = new Set<() => void>();
  let disposed = false;
  // Side effects run between `start` and `stop` only: a core built during a
  // render (and perhaps thrown away with it) does nothing until mounted.
  let running = false;
  let scheduled = false;
  let reconciling = false;

  const set = (patch: StatePatch) => {
    if (disposed) return;
    const changes = typeof patch === "function" ? patch(state) : patch;
    const keys = Object.keys(changes) as Array<keyof CoreState>;
    if (keys.every((key) => Object.is(changes[key], state[key]))) return;
    const next = { ...state, ...changes };
    // Once a payment has started here its status is told to the end.
    if (next.payingFlag) next.paidHere = true;
    state = next;
    snapshot = null;
    for (const listener of listeners) listener();
    if (!reconciling && running) schedule();
  };
  // The first time the checkout has its session: a read that follows up on
  // something (the cart re-quoted for an attested buyer) waits for it.
  let markLoaded: () => void = () => undefined;
  const loaded = new Promise<void>((resolve) => (markLoaded = resolve));
  const dispatch = (event: CheckoutEvent) => {
    const next = checkoutReducer(state.checkout, event);
    if (next !== state.checkout) set({ checkout: next });
    if ("session" in next) markLoaded();
  };
  // Reads of the session can cross on the network — an attestation's slow
  // refresh and the poll after paying, say — and the one made later is the
  // newer. Each is numbered as it is made, and its answer is applied only
  // if no later-made read's (or write's) has been already. A write is
  // numbered as it is sent and its answer, its own change's, is always
  // applied: a read made before it is older, however late it lands (a
  // refresh landing after a free order completed, or after the engine
  // settled a payment the moment it moved, is not news). And a session
  // that has ended stays ended whatever a read says (state-machine.ts).
  let requestsMade = 0;
  let newestApplied = 0;
  const sessionRead = () => {
    const read = ++requestsMade;
    return (event: SessionEvent) => {
      if (read < newestApplied) return false;
      newestApplied = read;
      dispatch(event);
      return true;
    };
  };
  const sessionWrite = () => {
    const write = ++requestsMade;
    return (event: SessionEvent) => {
      newestApplied = Math.max(newestApplied, write);
      dispatch(event);
    };
  };
  // Nothing reaches the page from a stopped core: a payment or an email
  // write still in flight when it was unmounted is not the page's news.
  const emit = (message: FrameMessage) => {
    if (running) deps.events.emit(message);
  };
  const emitted = new Set<string>();
  const emitOnce = (key: string, message: FrameMessage) => {
    if (emitted.has(key)) return;
    emitted.add(key);
    emit(message);
  };

  const ctx: CoreContext = {
    deps,
    config,
    live: () => state,
    set,
    dispatch,
    sessionRead,
    sessionWrite,
    loaded,
    emit,
    emitOnce,
    refs: { ...initialRefs(), emitted },
  };
  const identity = createIdentityFlows(ctx);
  const email = createEmailFlows(ctx, identity);
  const pay = createPayFlows(ctx, identity, email);

  /** The page is told what it needs to draw its own ending, from exactly
   *  where the status stands. */
  const tellSucceeded = () => {
    const session = sessionOf(state);
    if (!session) return;
    const statusOrigin = deps.events.statusOrigin();
    if (!config.hostIdentity) set({ successShown: true });
    emitOnce("succeeded", {
      type: "succeeded",
      total_label: money(
        session.total_minor,
        session.currency,
        state.inputs.locale ?? undefined,
      ),
      method_label: state.methodLabel,
      buyer_email: session.buyer_email ?? state.attested?.email ?? null,
      status_origin: statusOrigin,
    });
  };
  const timeline = createPayTimeline({
    clock: deps.clock,
    onChange: (view) => set({ pay: view }),
    // "You're going" has been seen: outside host mode the checkout's own
    // success screen follows.
    onDone: tellSucceeded,
  });
  /** A zero-total session has no status steps to wait out: its ending is
   *  told the moment the order is issued, whoever answered for the buyer. */
  const succeedFree: Reaction<CoreState> = {
    deps: (s) => [s.checkout.kind === "success" && isZeroTotal(s)],
    run: (s) => {
      if (s.checkout.kind === "success" && isZeroTotal(s)) tellSucceeded();
    },
  };
  // A free order for a buyer the host has already answered for: nothing to
  // choose and nothing to pay, so it completes once the engine has attested
  // them — no "Get tickets" to press after Continue. Once per checkout: a
  // refusal leaves the button to try again.
  let freeAsked = false;
  const completeFreeForHost: Reaction<CoreState> = {
    deps: (s) => [
      config.hostIdentity &&
        s.checkout.kind === "cart" &&
        isZeroTotal(s) &&
        Boolean(s.attested) &&
        s.busy === null,
    ],
    run: (s) => {
      if (
        freeAsked ||
        !config.hostIdentity ||
        s.checkout.kind !== "cart" ||
        !isZeroTotal(s) ||
        !s.attested ||
        s.busy !== null
      )
        return;
      freeAsked = true;
      void email.completeFree(s);
    },
  };
  const reactions = createReactions([
    ...checkoutReactions(ctx, identity, timeline, pay),
    completeFreeForHost,
    succeedFree,
  ]);

  /** One pass of reactions over the state as it stands. What they change
   *  themselves is reacted to on the next turn, as a re-render's would be. */
  function reconcile() {
    scheduled = false;
    if (disposed || !running) return;
    const view = state;
    reconciling = true;
    try {
      reactions.run(view);
    } finally {
      reconciling = false;
    }
    if (state !== view) schedule();
  }
  function schedule() {
    if (scheduled || disposed || !running) return;
    scheduled = true;
    deps.clock.setTimeout(reconcile, 0);
  }

  let stopIdentity: (() => void) | null = null;
  const start = () => {
    if (running || disposed) return;
    running = true;
    stopIdentity =
      broker?.subscribe(() => set({ identity: brokerView() })) ?? null;
    set({ identity: brokerView() });
    timeline.resume();
    schedule();
  };
  const stop = () => {
    if (!running) return;
    running = false;
    stopIdentity?.();
    stopIdentity = null;
    reactions.reset();
    timeline.pause();
    email.clearDebounce();
  };

  // Every action reads the state as it is at the press, and carries that
  // view through whatever it awaits (see context.ts).
  const actions: CheckoutActions = {
    typeEmail: (value) => email.typeEmail(state, value),
    settleEmail: () => email.settleEmail(state),
    submitEmail: () => email.submitEmail(state),
    changeContact: () => {
      // A fixed address has nothing to change it to: no × is drawn for it.
      if (config.hostIdentity) {
        if (!config.fixedContact) emit({ type: "change_contact" });
      } else void identity.removeEmail(state);
    },
    continueAsGuest: () => {
      if (broker && state.identity.connected) void broker.cancelCode();
    },
    // The code step is the broker's: without one there is no code to check.
    submitCode: async (code) => {
      const view = state;
      if (!broker) throw new Error("identity_unavailable");
      const result = await broker.verifyCode(code);
      void pay.codeVerified(view, result.token);
    },
    resendCode: () =>
      broker
        ? broker.resendCode()
        : Promise.reject(new Error("identity_unavailable")),
    showNotice: (message) => set({ notice: message }),
    setRemember: (on) => set({ remember: on }),
    selectCard: (id) => set({ selected: id }),
    paySaved: (card) => void pay.paySaved(state, card),
    payWithNewCard: (card, brandHint) =>
      void pay.payWithNewCard(state, card, brandHint),
    methodPending: pay.methodPending,
    payExpress: (paymentMethodId, sheetEmail, label) =>
      void pay.payExpress(state, paymentMethodId, sheetEmail, label),
    paymentFailed: pay.failed,
    bankDone: pay.bankDone,
    completeFree: () => void email.completeFree(state),
    fieldFocused: ({ top, bottom }) =>
      emit({ type: "focus", top: Math.round(top), bottom: Math.round(bottom) }),
  };

  return {
    getSnapshot: () => (snapshot ??= snapshotOf(state, config)),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    update: (next) => {
      const merged = { ...state.inputs, ...next };
      const same = (Object.keys(merged) as Array<keyof CheckoutInputs>).every(
        (key) => Object.is(merged[key], state.inputs[key]),
      );
      if (!same) set({ inputs: merged });
    },
    actions,
    start,
    stop,
    dispose: () => {
      if (disposed) return;
      stop();
      timeline.dispose();
      disposed = true;
      listeners.clear();
    },
  };
}
